// challengeHelpers.js
// Pure helper functions for 21-Day Challenge day/streak/completion math.
// Kept framework-free (no redux/React imports) so they are reusable from
// the reducer, components, and unit tests without any store setup.

import { CHALLENGE_DAY_STATUS, CHALLENGE_RULES } from "constants/ChallengeConfig";

/**
 * Builds the initial 21-day grid for a freshly activated enrollment.
 * Day 1 starts as "today"; the rest are "pending".
 * @returns {Array<{dayIndex: number, status: string, score: number|null, submittedAt: string|null}>}
 */
export const buildInitialChallengeDays = () =>
  Array.from({ length: CHALLENGE_RULES.TOTAL_DAYS }, (_, i) => ({
    dayIndex: i + 1,
    status: i === 0 ? CHALLENGE_DAY_STATUS.TODAY : CHALLENGE_DAY_STATUS.PENDING,
    score: null,
    submittedAt: null,
  }));

/**
 * Counts days with status SUBMITTED or FROZEN — both count toward the
 * completion threshold since a frozen day preserves the streak.
 */
export const countValidDays = (days) =>
  days.filter(
    (day) =>
      day.status === CHALLENGE_DAY_STATUS.SUBMITTED ||
      day.status === CHALLENGE_DAY_STATUS.FROZEN
  ).length;

/** THE SYSTEM SHALL consider the challenge lessons-complete once the
 * configured minimum number of valid days is reached. */
export const isLessonsComplete = (days) =>
  countValidDays(days) >= CHALLENGE_RULES.COMPLETION_MIN_DAYS;

/**
 * Applies today's practice submission to the day grid: marks the current
 * "today" day as submitted, then either advances to the next pending day
 * (marking it "today") or, if this was the last day, leaves the grid as-is
 * for the caller to evaluate completion.
 * @param {Array} days
 * @param {number} score - mock accuracy score 0-100
 * @returns {Array} new days array (does not mutate input)
 */
export const applyPracticeSubmission = (days, score) => {
  const todayIndex = days.findIndex((day) => day.status === CHALLENGE_DAY_STATUS.TODAY);
  if (todayIndex === -1) return days;

  const next = days.map((day, i) =>
    i === todayIndex
      ? {
          ...day,
          status: CHALLENGE_DAY_STATUS.SUBMITTED,
          score,
          submittedAt: new Date().toISOString(),
        }
      : day
  );

  const nextPendingIndex = next.findIndex(
    (day, i) => i > todayIndex && day.status === CHALLENGE_DAY_STATUS.PENDING
  );
  if (nextPendingIndex !== -1) {
    next[nextPendingIndex] = { ...next[nextPendingIndex], status: CHALLENGE_DAY_STATUS.TODAY };
  }

  return next;
};

/**
 * Consumes a streak freeze on the current "today" day (used by the demo
 * controls to simulate a missed day being covered by a freeze) and advances
 * to the next day, mirroring applyPracticeSubmission's advance behaviour.
 */
export const applyFreeze = (days) => {
  const todayIndex = days.findIndex((day) => day.status === CHALLENGE_DAY_STATUS.TODAY);
  if (todayIndex === -1) return days;

  const next = days.map((day, i) =>
    i === todayIndex ? { ...day, status: CHALLENGE_DAY_STATUS.FROZEN } : day
  );

  const nextPendingIndex = next.findIndex(
    (day, i) => i > todayIndex && day.status === CHALLENGE_DAY_STATUS.PENDING
  );
  if (nextPendingIndex !== -1) {
    next[nextPendingIndex] = { ...next[nextPendingIndex], status: CHALLENGE_DAY_STATUS.TODAY };
  }

  return next;
};

/** Marks the current "today" day as missed (no freeze available) and advances. */
export const applyMiss = (days) => {
  const todayIndex = days.findIndex((day) => day.status === CHALLENGE_DAY_STATUS.TODAY);
  if (todayIndex === -1) return days;

  const next = days.map((day, i) =>
    i === todayIndex ? { ...day, status: CHALLENGE_DAY_STATUS.MISSED } : day
  );

  const nextPendingIndex = next.findIndex(
    (day, i) => i > todayIndex && day.status === CHALLENGE_DAY_STATUS.PENDING
  );
  if (nextPendingIndex !== -1) {
    next[nextPendingIndex] = { ...next[nextPendingIndex], status: CHALLENGE_DAY_STATUS.TODAY };
  }

  return next;
};

/** Current consecutive streak counting back from the last finalized day. */
export const computeCurrentStreak = (days) => {
  let streak = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    const { status } = days[i];
    if (status === CHALLENGE_DAY_STATUS.SUBMITTED || status === CHALLENGE_DAY_STATUS.FROZEN) {
      streak++;
    } else if (status === CHALLENGE_DAY_STATUS.MISSED) {
      break;
    }
  }
  return streak;
};

/** Whether every day has been finalized (no PENDING/TODAY days remain). */
export const isChallengeWindowClosed = (days) =>
  days.every(
    (day) => day.status !== CHALLENGE_DAY_STATUS.PENDING && day.status !== CHALLENGE_DAY_STATUS.TODAY
  );

export const countFreezesUsed = (days) =>
  days.filter((day) => day.status === CHALLENGE_DAY_STATUS.FROZEN).length;
